import { TFunction } from 'i18next';
import { Tool } from '../data/tools';

/**
 * 搜索工具的配置选项
 */
export interface SearchOptions {
  /** 是否启用模糊搜索 */
  fuzzy?: boolean;
  /** 是否区分大小写 */
  caseSensitive?: boolean;
  /** 最小匹配长度 */
  minLength?: number;
  /** 多关键词搜索策略 */
  multiKeywordStrategy?: 'all' | 'any';
  /** 搜索权重配置 */
  weights?: {
    name: number;
    description: number;
    keywords: number;
    longDescription: number;
  };
}

/**
 * 搜索结果项
 */
export interface SearchResult {
  tool: Tool;
  score: number;
  matchedFields: string[];
}

/**
 * 默认搜索配置
 */
const DEFAULT_OPTIONS: Required<SearchOptions> = {
  fuzzy: true,
  caseSensitive: false,
  minLength: 1,
  multiKeywordStrategy: 'all',
  weights: {
    name: 3,
    description: 2,
    keywords: 2,
    longDescription: 1,
  },
};

/**
 * 计算字符串相似度（简单的编辑距离算法）
 */
function calculateSimilarity(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;
  
  if (len1 === 0) return len2;
  if (len2 === 0) return len1;
  
  const matrix = Array(len1 + 1).fill(null).map(() => Array(len2 + 1).fill(null));
  
  for (let i = 0; i <= len1; i++) matrix[i][0] = i;
  for (let j = 0; j <= len2; j++) matrix[0][j] = j;
  
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }
  
  const maxLen = Math.max(len1, len2);
  return (maxLen - matrix[len1][len2]) / maxLen;
}

/**
 * 检查是否包含查询字符串
 */
function containsQuery(text: string, query: string, options: Required<SearchOptions>): boolean {
  const searchText = options.caseSensitive ? text : text.toLowerCase();
  const searchQuery = options.caseSensitive ? query : query.toLowerCase();

  // 将查询分割成多个关键词
  const queryKeywords = searchQuery.split(/\s+/).filter(keyword => keyword.length > 0);

  if (queryKeywords.length === 0) return false;

  // 根据策略检查关键词匹配
  const checkKeyword = (keyword: string) => {
    if (options.fuzzy) {
      // 模糊搜索：检查相似度
      const textWords = searchText.split(/\s+/);
      return textWords.some(word => {
        if (word.includes(keyword)) return true;
        return calculateSimilarity(word, keyword) > 0.7;
      });
    } else {
      // 精确搜索：检查是否包含关键词
      return searchText.includes(keyword);
    }
  };

  if (options.multiKeywordStrategy === 'all') {
    // 所有关键词都必须匹配
    return queryKeywords.every(checkKeyword);
  } else {
    // 任意关键词匹配即可
    return queryKeywords.some(checkKeyword);
  }
}

/**
 * 计算匹配分数
 */
function calculateScore(
  tool: Tool,
  query: string,
  t: TFunction,
  options: Required<SearchOptions>
): { score: number; matchedFields: string[] } {
  const fields = {
    name: t(tool.name),
    description: t(tool.description),
    keywords: t(tool.keywords),
    longDescription: t(tool.longDescription),
  };

  let totalScore = 0;
  const matchedFields: string[] = [];

  Object.entries(fields).forEach(([fieldName, fieldValue]) => {
    if (containsQuery(fieldValue, query, options)) {
      const weight = options.weights[fieldName as keyof typeof options.weights];

      // 计算字段内的匹配分数
      let fieldScore = 0;
      const searchText = options.caseSensitive ? fieldValue : fieldValue.toLowerCase();
      const searchQuery = options.caseSensitive ? query : query.toLowerCase();

      // 将查询分割成多个关键词
      const queryKeywords = searchQuery.split(/\s+/).filter(keyword => keyword.length > 0);

      if (queryKeywords.length === 0) {
        fieldScore = 0;
      } else {
        // 计算每个关键词的匹配分数
        const keywordScores = queryKeywords.map(keyword => {
          let keywordScore = 0;

          if (searchText.includes(keyword)) {
            // 精确匹配得分
            keywordScore = 1;

            // 如果是完全匹配，额外加分
            if (searchText === keyword) {
              keywordScore = 1.5;
            }
            // 如果是开头匹配，额外加分
            else if (searchText.startsWith(keyword)) {
              keywordScore = 1.2;
            }
          } else if (options.fuzzy) {
            // 模糊匹配分数
            const words = searchText.split(/\s+/);
            const similarities = words.map((word: string) => calculateSimilarity(word, keyword));
            const maxSimilarity = Math.max(...similarities);
            keywordScore = maxSimilarity > 0.7 ? maxSimilarity : 0;
          }

          return keywordScore;
        });

        // 使用所有关键词分数的平均值，但给予额外的多关键词匹配奖励
        const averageScore = keywordScores.reduce((sum, score) => sum + score, 0) / keywordScores.length;
        const matchedKeywords = keywordScores.filter(score => score > 0).length;
        const matchRatio = matchedKeywords / queryKeywords.length;

        // 基础分数 * 匹配比例 + 多关键词奖励
        fieldScore = averageScore * matchRatio + (matchedKeywords > 1 ? 0.2 : 0);
      }

      if (fieldScore > 0) {
        totalScore += fieldScore * weight;
        matchedFields.push(fieldName);
      }
    }
  });

  return { score: totalScore, matchedFields };
}

/**
 * 搜索工具
 */
export function searchTools(
  tools: Tool[],
  query: string,
  t: TFunction,
  options: Partial<SearchOptions> = {}
): SearchResult[] {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };

  // 检查查询长度
  if (!query || query.trim().length < mergedOptions.minLength) {
    return [];
  }

  const trimmedQuery = query.trim();
  const results: SearchResult[] = [];

  tools.forEach(tool => {
    const { score, matchedFields } = calculateScore(tool, trimmedQuery, t, mergedOptions);

    if (score > 0) {
      results.push({
        tool,
        score,
        matchedFields,
      });
    }
  });

  // 按分数排序
  return results.sort((a, b) => b.score - a.score);
}

/**
 * 高亮搜索结果
 */
export function highlightSearchTerm(text: string, query: string, caseSensitive = false): string {
  if (!query || !text) return text;
  
  const searchText = caseSensitive ? text : text.toLowerCase();
  const searchQuery = caseSensitive ? query : query.toLowerCase();
  
  const index = searchText.indexOf(searchQuery);
  if (index === -1) return text;
  
  const before = text.substring(0, index);
  const match = text.substring(index, index + query.length);
  const after = text.substring(index + query.length);
  
  return `${before}<mark class="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">${match}</mark>${after}`;
}

/**
 * 获取搜索建议
 */
export function getSearchSuggestions(
  tools: Tool[],
  query: string,
  t: TFunction,
  limit = 5
): string[] {
  if (!query || query.length < 2) return [];
  
  const suggestions = new Set<string>();
  const lowerQuery = query.toLowerCase();
  
  tools.forEach(tool => {
    const fields = [
      t(tool.name),
      t(tool.description),
      t(tool.keywords),
    ];
    
    fields.forEach(field => {
      const words = field.toLowerCase().split(/\s+/);
      words.forEach((word: string) => {
        if (word.startsWith(lowerQuery) && word.length > lowerQuery.length) {
          suggestions.add(word);
        }
      });
    });
  });
  
  return Array.from(suggestions).slice(0, limit);
}
